export interface BaseUser {
  id: number;
  email: string;
  isNanny: boolean;
  isPartner: boolean;
  isContractor: boolean;
  isClient: boolean;
  isSuperUser: boolean;
  inImplementation: boolean;
  demo_account?: boolean;
  date_joined?: string;
  hasActiveSubscription: boolean;
  subscriptionStatus: string | null;
}

export interface SuperUser extends BaseUser {
  userType: "superuser";
}

export interface FullUser extends BaseUser {
  userType: "employer";
}

export interface NannyUser extends BaseUser {
  userType: "nanny";
  employerEmployeeId: number;
  nannyDetails?: {
    employerEmployeeId: number;
  };
}

export interface ContractorUser extends BaseUser {
  userType: "CONTRACTOR";
}

export interface PartnerUser extends BaseUser {
  userType: "PARTNER";
  companyName: string;
  partnerDetails?: {
    companyName: string;
  };
}

export interface ClientUser extends BaseUser {
  userType: "CLIENT";
  clientDetails?: {
    clientUserId: number;
  };
}

export type User =
  | FullUser
  | NannyUser
  | SuperUser
  | PartnerUser
  | ContractorUser
  | ClientUser;

export interface UserState {
  user: User | null;
  isAuthenticated: boolean;
}

export const isPartnerUser = (user: User): user is PartnerUser => {
  return user.userType === "PARTNER";
};

export const isNannyUser = (user: User | null): user is NannyUser => {
  return user?.userType === "nanny";
};

export const isFullUser = (user: User | null): user is FullUser => {
  return user?.userType === "employer";
};

export const isSuperUser = (user: User | null): user is SuperUser => {
  return user?.userType === "superuser";
};

export const isContractorUser = (user: User | null): user is ContractorUser => {
  return user?.userType === "CONTRACTOR";
};

export const isClientUser = (user: User | null): user is ClientUser => {
  return user?.userType === "CLIENT" || user?.isClient === true;
};

// Check if user requires an active subscription to access the platform
export const requiresActiveSubscription = (user: User | null): boolean => {
  if (!user) return false;
  
  // Super users and demo accounts don't require subscriptions
  if (user.isSuperUser || user.demo_account) return false;
  
  // Partners (affiliates) don't require subscriptions for their referral dashboard
  if (user.isPartner) return false;
  
  // All other user types (employers, nannies, contractors, clients) require an active subscription
  return true;
};

// Check if user has access to the platform (either doesn't require subscription or has active one)
export const hasAccessToSystem = (user: User | null): boolean => {
  if (!user) return false;
  
  // If user doesn't require a subscription, they have access
  if (!requiresActiveSubscription(user)) return true;
  
  // If user requires subscription, check if they have an active one
  // Active subscription statuses are: "active" and "trialing"
  return user.hasActiveSubscription;
};

export interface LoginResponse {
  access: string;
  refresh: string;
}

export interface UserDetailsResponse {
  id: number;
  email: string;
  userType: "employer" | "nanny" | "PARTNER" | "CONTRACTOR" | "CLIENT";
  isNanny: boolean;
  isSuperUser: boolean;
  isClient: boolean;
  inImplementation: boolean;
  demo_account?: boolean;
  date_joined?: string;
  hasActiveSubscription: boolean;
  subscriptionStatus: string | null;
  nannyDetails?: {
    employerEmployeeId: number;
  };
  partnerDetails?: {
    companyName: string;
  };
  clientDetails?: {
    clientUserId: number;
  };
}

export interface RegisterRequest {
  email: string;
  password1: string;
  password2: string;
  user_type: "FULL" | "PARTNER" | "CONTRACTOR" | "CLIENT";
  company_name?: string; // Required for PARTNER
}

export interface RegisterResponse {
  user: {
    id: number;
    email: string;
    user_type: "FULL" | "PARTNER" | "CONTRACTOR" | "CLIENT";
  };
  partner_details?: {
    company_name: string;
    referral_code: string;
  };
}
